import { uniq } from "lodash-es";
import { useForm } from "react-hook-form";

import type { ProjectConnectionDetailsDto } from "@/api/types";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { Form } from "@/components/Form/Form";
import { FormImageInput } from "@/components/Form/FormImageInput";
import { FormInput } from "@/components/Form/FormInput";
import { FormMultiSelect } from "@/components/Form/FormMultiSelect";
import { FormTextArea } from "@/components/Form/FormTextArea";
import type { FormImage } from "@/components/ImageInput/useImageInput";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { PillList } from "@/components/Pill/PillList";
import { Button } from "@/design-system/Button";
import { FormField } from "@/design-system/FormField";
import { createImageSizeRule, createImageTypeRule, createRequiredStringRule } from "@/helpers/rules";
import { sortAlphabetically } from "@/helpers/util";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

interface Project {
  id: string;
  name: string;
  baseLanguageId: string;
}
export interface ProjectConnectionFormProps {
  isEditMode: boolean;
  defaultFormValues: Partial<FormValues>;
  projects: Project[];
  otherConnections: ProjectConnectionDetailsDto[];
  isSubmitting: boolean;
  onSubmit: (value: FormValues) => void;
}
const NAME_MAX_LENGTH = 255;
const DESCRIPTION_MAX_LENGTH = 255;
export function ProjectConnectionForm({
  defaultFormValues,
  isEditMode,
  isSubmitting,
  onSubmit,
  otherConnections,
  projects,
}: ProjectConnectionFormProps): React.ReactNode {
  const { t } = useTranslation();
  const form = useForm<FormValues>({
    defaultValues: defaultFormValues,
  });
  const title = isEditMode ? t("page.project-connections.edit.title") : t("page.project-connections.create.title");

  return (
    <DocumentPage
      containerBoundary="inherit"
      contentSize="constrained"
      tabTitle={title}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            {
              name: t("page.project-connections.title"),
              to: routes.projectConnections.list(),
            },
            {
              name: title,
            },
          ]}
        />
      }
    >
      <Form.Root formMethods={form} onSubmit={onSubmit}>
        <Form.Container>
          <Form.Body>
            <FormField label={t("page.project-connections.form.name")}>
              <FormInput<FormValues, "name">
                name="name"
                id="name"
                rules={{
                  validate: {
                    required: createRequiredStringRule(t, "page.project-connections.form.name"),
                    isDuplicate(value) {
                      const trimmedValue = value.toLowerCase().trim();
                      if (otherConnections.some((x) => x.name.toLowerCase() === trimmedValue)) {
                        return t("page.project-connections.form.name.error.already-exists");
                      }
                    },
                  },
                  maxLength: {
                    message: t("components.form.error.max-length", {
                      length: NAME_MAX_LENGTH,
                    }),
                    value: NAME_MAX_LENGTH,
                  },
                }}
              />
            </FormField>
            <FormField label={t("page.project-connections.form.description")}>
              <FormInput<FormValues>
                name="description"
                rules={{
                  maxLength: {
                    message: t("components.form.error.max-length", {
                      length: DESCRIPTION_MAX_LENGTH,
                    }),
                    value: DESCRIPTION_MAX_LENGTH,
                  },
                }}
              />
            </FormField>
            <FormField label={t("page.project-connections.form.projects")}>
              {isEditMode ? (
                <PillList
                  items={
                    defaultFormValues.projects?.map((project) => ({
                      label: project.name,
                    })) ?? []
                  }
                />
              ) : (
                <FormMultiSelect<FormValues, "projects">
                  name="projects"
                  items={projects}
                  sortSelectedItems={sortAlphabetically}
                  keySelector={(x) => x.id}
                  renderOption={(x) => x.name}
                  placeholder={t("page.project-connections.form.projects.placeholder")}
                  rules={{
                    validate: (value) => {
                      if (!isEditMode) {
                        if (!Array.isArray(value) || value.length < 2) {
                          return t("page.project-connections.form.projects.error.need-two-or-more");
                        }
                        if (isDuplicate(value, otherConnections)) {
                          return t("page.project-connections.form.projects.error.duplicate");
                        }
                        if (new Set(value.map((x) => x.baseLanguageId)).size > 1) {
                          return t("page.project-connections.form.projects.error.different-languages");
                        }
                      }
                    },
                  }}
                />
              )}
            </FormField>
            <FormField label={t("page.project-connections.form.background-image")} required>
              <FormImageInput<FormValues, "backgroundImage">
                name="backgroundImage"
                rules={{
                  required: t("components.form.error.required", {
                    inputName: t("page.project-connections.form.background-image"),
                  }),
                  validate: {
                    size: createImageSizeRule({
                      t,
                    }),
                    type: createImageTypeRule({
                      t,
                    }),
                  },
                }}
              />
            </FormField>
            <FormField required label={t("page.project-connections.form.explanation")}>
              <FormTextArea<FormValues>
                width="wide"
                name="content"
                rules={{
                  validate: {
                    required: createRequiredStringRule(t, "page.project-connections.form.explanation"),
                  },
                }}
              />
            </FormField>
          </Form.Body>
          <Form.Footer
            renderPrimaryAction={() => (
              <Button type="submit" isLoading={isSubmitting}>
                {isEditMode ? t("common.action.save") : t("common.action.create")}
              </Button>
            )}
          />
        </Form.Container>
      </Form.Root>
    </DocumentPage>
  );
}
export interface FormValues {
  name: string;
  description: string;
  projects: Project[];
  content: string;
  backgroundImage: FormImage[];
}
function isDuplicate(
  newConnection: {
    id: string;
    name: string;
  }[],
  existingConnections: ProjectConnectionDetailsDto[],
) {
  const newConnectionString = uniq(newConnection.map((p) => p.id))
    .sort()
    .join("|||");
  const existingConnectionsStrings = existingConnections.map((c) =>
    c.projects
      .map((p) => p.id)
      .sort()
      .join("|||"),
  );
  for (const existingConnectionString of existingConnectionsStrings) {
    if (existingConnectionString === newConnectionString) {
      return true;
    }
  }

  return false;
}
